import { useState } from "react";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Search, FilterX } from "lucide-react";

interface CitizenSearchProps {
  initialQuery: string;
  onSearch: (query: string) => void;
  orderBy: string;
  onOrderChange: (value: string) => void;
  isVerified: string | null;
  onVerifiedChange: (value: string | null) => void;
  isActive: string | null;
  onActiveChange: (value: string | null) => void;
  onResetFilters: () => void;
}

export function CitizenSearch({
  initialQuery,
  onSearch,
  orderBy,
  onOrderChange,
  isVerified,
  onVerifiedChange,
  isActive,
  onActiveChange,
  onResetFilters,
}: CitizenSearchProps) {
  const [value, setValue] = useState(initialQuery);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch(value);
  };

  const hasActiveFilters =
    isVerified !== null || isActive !== null || orderBy !== "modified_date";

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 md:flex-row">
        <form onSubmit={handleSubmit} className="flex w-full max-w-sm">
          <Input
            type="search"
            placeholder="Search citizens..."
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="rounded-r-none"
          />
          <Button
            type="submit"
            variant="default"
            className="rounded-l-none px-3"
          >
            <Search className="h-4 w-4" />
          </Button>
        </form>

        <div className="flex flex-1 flex-wrap gap-2">
          <Select
            value={orderBy}
            onValueChange={(value) => onOrderChange(value)}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Sort by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="name">Name (A-Z)</SelectItem>
              <SelectItem value="score">Score (High-Low)</SelectItem>
              <SelectItem value="created_date">Created Date</SelectItem>
              <SelectItem value="modified_date">Last Modified</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={isVerified === null ? "all_statuses" : isVerified}
            onValueChange={(value) =>
              onVerifiedChange(value === "all_statuses" ? null : value)
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Verification Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_statuses">All Statuses</SelectItem>
              <SelectItem value="true">Verified</SelectItem>
              <SelectItem value="false">Unverified</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={isActive === null ? "all_statuses" : isActive}
            onValueChange={(value) =>
              onActiveChange(value === "all_statuses" ? null : value)
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Active Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all_statuses">All</SelectItem>
              <SelectItem value="true">Active</SelectItem>
              <SelectItem value="false">Inactive</SelectItem>
            </SelectContent>
          </Select>

          {hasActiveFilters && (
            <Button
              variant="outline"
              size="icon"
              onClick={onResetFilters}
              title="Reset filters"
            >
              <FilterX className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
